import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Loader2, Plus, Search, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getMunicipalityName } from "@facturar/locations";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TerceroSearchInput } from "@/components/terceros/TerceroSearchInput";
import { useTerceros } from "@/lib/api/terceros.queries";
import { useActiveCompany } from "@/lib/company-context";
import { cn } from "@/lib/cn";
import type {
  TerceroKind,
  TerceroListStatusFilter,
  TerceroSummary,
} from "@/lib/api/terceros";

export const Route = createFileRoute("/companies/$companyId/terceros/")({
  component: TercerosIndexPage,
});

type KindFilter = "all" | TerceroKind;

function TercerosIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <TercerosIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function TercerosIndexContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  // Sync the URL companyId into the active context so the sidebar + header
  // reflect it. Mirrors the company detail page's behaviour.
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [statusFilter, setStatusFilter] =
    useState<TerceroListStatusFilter>("active");
  const [q, setQ] = useState("");

  const filters = useMemo(
    () => ({
      ...(q ? { q } : {}),
      ...(kindFilter !== "all" ? { kind: kindFilter as TerceroKind } : {}),
      status: statusFilter,
      pageSize: 50,
    }),
    [q, kindFilter, statusFilter],
  );

  const tercerosQuery = useTerceros(companyId, filters);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {t("terceros.index.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {t("terceros.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("terceros.index.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link
              to="/companies/$companyId/terceros/new"
              params={{ companyId }}
              search={{ openLookup: "1" }}
            >
              <Search className="h-4 w-4" />
              {t("terceros.index.lookupCta")}
            </Link>
          </Button>
          <Button asChild>
            <Link
              to="/companies/$companyId/terceros/new"
              params={{ companyId }}
            >
              <Plus className="h-4 w-4" />
              {t("terceros.new")}
            </Link>
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <TerceroSearchInput value={q} onChange={setQ} />
          <div className="grid gap-1.5">
            <label
              htmlFor="tercero-status-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t("terceros.filter.showLabel")}
            </label>
            <Select
              value={statusFilter}
              onValueChange={(v) =>
                setStatusFilter(v as TerceroListStatusFilter)
              }
            >
              <SelectTrigger
                id="tercero-status-filter"
                className="w-[180px]"
                aria-label={t("terceros.filter.showLabel")}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">
                  {t("terceros.filter.active")}
                </SelectItem>
                <SelectItem value="archived">
                  {t("terceros.filter.archived")}
                </SelectItem>
                <SelectItem value="all">{t("terceros.filter.all")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {statusFilter !== "active" ? (
          <p className="text-xs text-muted-foreground">
            {t("terceros.filter.archivedHint")}
          </p>
        ) : null}
      </div>

      <KindFilterChips value={kindFilter} onChange={setKindFilter} />

      {tercerosQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : tercerosQuery.isError ? (
        <ErrorState
          title={t("terceros.index.error.title")}
          description={t("terceros.index.error.body")}
          onRetry={() => void tercerosQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : (tercerosQuery.data?.terceros.length ?? 0) === 0 ? (
        <EmptyState
          companyId={companyId}
          hasFilters={
            q.length > 0 ||
            kindFilter !== "all" ||
            statusFilter === "archived"
          }
          showingArchived={statusFilter === "archived"}
        />
      ) : (
        <TercerosTable
          companyId={companyId}
          terceros={tercerosQuery.data!.terceros}
        />
      )}
    </div>
  );
}

function TercerosTable({
  companyId,
  terceros,
}: {
  companyId: string;
  terceros: TerceroSummary[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<TerceroSummary>[]>(
    () => [
      {
        id: "identification",
        header: t("terceros.table.identification"),
        enableSorting: false,
        meta: { headClassName: "w-[170px]", cellClassName: "font-mono text-xs" },
        cell: ({ row }) => {
          const tercero = row.original;
          return (
            <>
              <span className="text-muted-foreground">
                {tercero.identificationType}
              </span>{" "}
              {tercero.identificationNumber}
              {tercero.dv ? (
                <span className="text-muted-foreground">-{tercero.dv}</span>
              ) : null}
            </>
          );
        },
      },
      {
        accessorKey: "legalName",
        header: t("terceros.table.name"),
        cell: ({ row }) => {
          const tercero = row.original;
          return (
            <>
              <span className="block max-w-[320px] truncate font-medium">
                {tercero.legalName}
              </span>
              {tercero.tradeName ? (
                <span className="block max-w-[320px] truncate text-xs text-muted-foreground">
                  {tercero.tradeName}
                </span>
              ) : null}
            </>
          );
        },
      },
      {
        accessorKey: "kind",
        header: t("terceros.table.kind"),
        meta: { headClassName: "w-[140px]" },
        cell: ({ row }) => (
          <Badge variant="muted">{t(`terceros.kind.${row.original.kind}`)}</Badge>
        ),
      },
      {
        id: "city",
        header: t("terceros.table.city"),
        enableSorting: false,
        meta: {
          headClassName: "hidden lg:table-cell",
          cellClassName: "hidden lg:table-cell",
        },
        cell: ({ row }) => (
          <span className="block max-w-[180px] truncate text-xs text-muted-foreground">
            {row.original.cityCode
              ? (getMunicipalityName(row.original.cityCode) ??
                row.original.cityCode)
              : "—"}
          </span>
        ),
      },
      {
        id: "regime",
        header: t("terceros.table.regime"),
        enableSorting: false,
        meta: {
          headClassName: "hidden lg:table-cell w-[130px]",
          cellClassName: "hidden lg:table-cell",
        },
        cell: ({ row }) => {
          const code = row.original.regimeCode;
          return code === "48" || code === "49" ? (
            <Badge variant="muted">{t(`terceros.table.regime${code}`)}</Badge>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
      {
        id: "contact",
        header: t("terceros.table.contact"),
        enableSorting: false,
        meta: {
          headClassName: "hidden md:table-cell",
          cellClassName: "hidden md:table-cell",
        },
        cell: ({ row }) => {
          const tercero = row.original;
          return (
            <>
              <span className="block max-w-[240px] truncate text-xs text-muted-foreground">
                {tercero.email ?? "—"}
              </span>
              {tercero.phone ? (
                <span className="block text-xs text-muted-foreground">
                  {tercero.phone}
                </span>
              ) : null}
            </>
          );
        },
      },
      {
        accessorKey: "status",
        header: t("terceros.table.status"),
        meta: { headClassName: "w-[110px]" },
        cell: ({ row }) => (
          <Badge variant={row.original.status === "active" ? "success" : "muted"}>
            {t(`terceros.filter.${row.original.status}`)}
          </Badge>
        ),
      },
    ],
    [t],
  );

  return (
    <DataTable
      columns={columns}
      data={terceros}
      getRowId={(tercero) => tercero.id}
      onRowClick={(tercero) =>
        void navigate({
          to: "/companies/$companyId/terceros/$terceroId",
          params: { companyId, terceroId: tercero.id },
        })
      }
    />
  );
}

function KindFilterChips({
  value,
  onChange,
}: {
  value: KindFilter;
  onChange: (next: KindFilter) => void;
}) {
  const { t } = useTranslation();
  const chips: { value: KindFilter; labelKey: string }[] = [
    { value: "all", labelKey: "terceros.filter.all" },
    { value: "customer", labelKey: "terceros.filter.customer" },
    { value: "supplier", labelKey: "terceros.filter.supplier" },
    { value: "both", labelKey: "terceros.filter.both" },
    { value: "employee", labelKey: "terceros.filter.employee" },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) => {
        const active = chip.value === value;
        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => onChange(chip.value)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              active
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {t(chip.labelKey)}
          </button>
        );
      })}
    </div>
  );
}

function EmptyState({
  companyId,
  hasFilters,
  showingArchived,
}: {
  companyId: string;
  hasFilters: boolean;
  showingArchived: boolean;
}) {
  const { t } = useTranslation();
  if (hasFilters) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <h2 className="text-lg font-semibold">
            {showingArchived
              ? t("terceros.empty.archived")
              : t("terceros.empty.search")}
          </h2>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Users className="h-8 w-8 text-primary" />
        </div>
        <div className="max-w-sm space-y-1">
          <h2 className="text-lg font-semibold">{t("terceros.empty.title")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("terceros.empty.body")}
          </p>
        </div>
        <Button asChild>
          <Link
            to="/companies/$companyId/terceros/new"
            params={{ companyId }}
          >
            <Plus className="h-4 w-4" />
            {t("terceros.new")}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
